import React, { useEffect, useMemo } from 'react';
import { ExternalLink, X } from 'lucide-react';
import type { BydaResultDocument } from '@/services/bydaService';
import './BydaResultPdfPreview.css';

type BydaResultPdfPreviewProps = {
  document: BydaResultDocument | null;
  onClose: () => void;
};

export function isBydaPreviewablePdf(document: BydaResultDocument | null | undefined): document is BydaResultDocument {
  if (!document?.downloadUrl) return false;
  const mimeType = String(document.mimeType || '').toLowerCase();
  const fileName = String(document.fileName || '').toLowerCase();
  return document.blobStatus === 'staged' && (mimeType.includes('pdf') || fileName.endsWith('.pdf'));
}

export function buildBydaInlinePreviewUrl(downloadUrl: string): string {
  const separator = downloadUrl.includes('?') ? '&' : '?';
  return `${downloadUrl}${separator}disposition=inline`;
}

export default function BydaResultPdfPreview({ document, onClose }: BydaResultPdfPreviewProps) {
  const previewUrl = useMemo(() => (
    document?.downloadUrl ? buildBydaInlinePreviewUrl(document.downloadUrl) : ''
  ), [document?.downloadUrl]);

  useEffect(() => {
    if (!document) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [document, onClose]);

  if (!document || !previewUrl) return null;

  return (
    <div className="byda-pdf-preview" role="dialog" aria-modal="true" aria-label={`Preview ${document.fileName}`} onClick={onClose}>
      <div className="byda-pdf-preview__panel" onClick={(event) => event.stopPropagation()}>
        <header className="byda-pdf-preview__header">
          <div className="byda-pdf-preview__title-wrap">
            <div className="byda-pdf-preview__eyebrow">BYDA staged result</div>
            <h2 className="byda-pdf-preview__title">{document.fileName}</h2>
          </div>
          <div className="byda-pdf-preview__actions">
            <a href={previewUrl} target="_blank" rel="noreferrer" className="byda-pdf-preview__icon-button" aria-label="Open PDF in new tab" title="Open PDF in new tab">
              <ExternalLink size={18} aria-hidden="true" />
            </a>
            <button type="button" className="byda-pdf-preview__icon-button" onClick={onClose} aria-label="Close PDF preview" title="Close PDF preview">
              <X size={20} aria-hidden="true" />
            </button>
          </div>
        </header>
        <iframe
          title={`PDF preview: ${document.fileName}`}
          className="byda-pdf-preview__frame"
          src={previewUrl}
        />
      </div>
    </div>
  );
}
